import * as React from "react";
import {
  AtAGlance,
  ClinicSnapshot,
  DashboardHero,
  PageHeader,
  RecentCallLog,
  TopBar,
} from "./dashboard-composites";
import { GlanceRow, HeroCell } from "./dashboard-rows";
import {
  SiteShell,
  SiteHeader,
  SiteBrand,
  SiteMain,
  SiteNav,
  SiteNavItem,
  SiteNavFlyout,
  SiteNavFlyoutItem,
  SiteFooter,
  SiteFooterGrid,
  SiteFooterColumn,
  SiteFooterBottom,
} from "./dashboard-shell";
import { Icon, StatusChip, Pill } from "./primitives";
import {
  IconAlert,
  IconCalendar,
  IconCheck,
  IconClock,
  IconDashboard,
  IconMail,
  IconSettings,
  IconUser,
} from "./icons";
import { Button } from "./primitives";
import { MobileAppHeader, MobileCard, MobileTabBar, PhoneFrame } from "./mobile";
import {
  CallDetailBody,
  CallDetailColumns,
  CallDetailNav,
  CallDetailPage,
  CallDetailRail,
  NextStepBanner,
  OutcomeHero,
  RelatedCallRow,
  RelatedCallsCard,
  AudioPlayer,
  ResultPill,
  TranscriptContainer,
  TranscriptRow,
} from "./call-detail";

// ============================================================================
// Shared shell — wraps a preview in the real SiteShell chrome
// ============================================================================
function PreviewShell({
  headerCrumb,
  headerStatus,
  productLabel,
  username = "mjoplin",
  children,
}: {
  headerCrumb: { crumbs: string[]; emphasized: string };
  headerStatus?: React.ReactNode;
  productLabel: string;
  username?: string;
  children: React.ReactNode;
}) {
  return (
    <SiteShell>
      <SiteHeader
        brand={<SiteBrand name="ConstructorFleet" logoSrc="/symbol-shield.webp" href="/" />}
        nav={
          <SiteNav>
            <SiteNavFlyout label="Products">
              <SiteNavFlyoutItem
                href="/products/bhg-call"
                label="BHG Check-in Assist"
                description="Daily IVR calls, your result by text."
              />
              <SiteNavFlyoutItem
                href="/products/co-ua-notifications"
                label="UA Notifications"
                description="Jurisdiction call-in automation."
              />
            </SiteNavFlyout>
            <SiteNavItem href="/dashboard" active>
              Dashboard
            </SiteNavItem>
          </SiteNav>
        }
        right={
          <>
            <span className="session-username">{username}</span>
            <Button variant="ghost" size="sm" aria-label="Settings">
              <IconSettings />
            </Button>
            <Button variant="ghost" size="sm" aria-label="Account">
              <IconUser />
            </Button>
          </>
        }
      />
      <SiteMain>
        <TopBar
          crumb={headerCrumb}
          status={headerStatus ?? <StatusChip>{productLabel} · live</StatusChip>}
        />
        <div className="page ds-stack">{children}</div>
      </SiteMain>
      <SiteFooter>
        <SiteFooterGrid>
          <SiteFooterColumn>
            <div className="footer-brand">
              <img src="/symbol-shield.webp" alt="" aria-hidden="true" />
              ConstructorFleet
            </div>
          </SiteFooterColumn>
          <SiteFooterColumn heading="Product">
            <ul>
              <li>
                <a href="/products/bhg-call">BHG Check-in Assist</a>
              </li>
              <li>
                <a href="/products/co-ua-notifications">UA Notifications</a>
              </li>
            </ul>
          </SiteFooterColumn>
        </SiteFooterGrid>
        <SiteFooterBottom>
          <span>© ConstructorFleet L.L.C.</span>
        </SiteFooterBottom>
      </SiteFooter>
    </SiteShell>
  );
}

// ============================================================================
// BHG — dashboard (scheduled)
// ============================================================================
export function BhgDashboardScheduledPreview() {
  return (
    <PreviewShell
      productLabel="Check-in Assist"
      username="mjoplin"
      headerCrumb={{ crumbs: ["Dashboard"], emphasized: "Check-in Assist" }}
      headerStatus={<StatusChip>Connected · synced 12:37 AM</StatusChip>}
    >
      <PageHeader
        headingLevel="h2"
        title={<>Good morning, mjoplin.</>}
        sub="Today's schedule, your latest call result, and a record of every check-in."
        meta={{
          label: "Today",
          value: "Tue, Apr 21, 2026",
          subValue: "02:47 AM · America/Denver",
        }}
      />
      <DashboardHero state="scheduled" cols={3}>
        <HeroCell
          cell="visit"
          kickerIcon={
            <Icon tone="danger">
              <IconAlert />
            </Icon>
          }
          kickerLabel="Action required"
          big="Scheduled today"
          bigTone="danger"
          desc="You are scheduled to visit BHG Denver Downtown today for Drug Screen. Go in before dosing closes at 3:00 PM."
        />
        <HeroCell
          cell="checkin"
          kickerIcon={
            <Icon tone="success">
              <IconCheck />
            </Icon>
          }
          kickerLabel="Today's check-in"
          big="Call completed."
          bigTone="success"
          desc="Your 12:52 AM call went through."
        />
        <HeroCell
          cell="dosing"
          kickerIcon={
            <Icon tone="warn">
              <IconClock />
            </Icon>
          }
          kickerLabel="Clinic dosing"
          big="Closes 3:00 PM"
          desc="Plan to arrive before closing."
        />
      </DashboardHero>
      <div className="grid">
        <ClinicSnapshot
          editAction={{ label: "Edit clinic →", href: "#" }}
          name="BHG Denver Downtown Treatment Center"
          addressLines={["5250 Leetsdale Drive, Suite 220 · Denver, CO 80246"]}
          phone="(303) 629-5293"
          hours={[
            { label: "Operating hours", value: "5:00a – 3:00p" },
            { label: "Medication hours", value: "5:00a – 3:00p" },
          ]}
          link={{ label: "View clinic details", href: "#" }}
        />
        <AtAGlance meta="updated 2:47 AM">
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Tomorrow's visit"
            subtitle="Updates after the next completed call"
            value="Unknown"
            valueNote="pending"
          />
          <GlanceRow
            icon={<IconClock />}
            iconTone="mute"
            title="Next call"
            subtitle="Wed, Apr 22 — based on your schedule"
            value="12:40 AM"
            valueNote="in 21h 53m"
          />
          <GlanceRow
            icon={<IconMail />}
            iconTone="brand"
            title="Notification delivery"
            subtitle="SMS + Email · daily at 00:40"
            value="On"
            valueNote="2 channels"
          />
        </AtAGlance>
      </div>
      <RecentCallLog
        wide
        meta="LAST 4 DAYS"
        rows={[
          {
            rail: "fail",
            title: "Drug Screen",
            pill: { label: "Scheduled", tone: "scr" },
            snippet:
              '"…scheduled to visit the treatment center by end of dosing hours on 04/20/2026."',
            when: { primary: "Apr 20", secondary: "12:35 AM" },
          },
          {
            rail: "ok",
            title: "Not scheduled",
            pill: { label: "Completed", tone: "ok" },
            snippet: '"…not scheduled to come into the treatment center on 04/20/2026."',
            when: { primary: "Apr 19", secondary: "12:35 AM" },
          },
          {
            rail: "ok",
            title: "Not scheduled",
            pill: { label: "Completed", tone: "ok" },
            snippet: '"…not scheduled to come into the treatment center on 04/19/2026."',
            when: { primary: "Apr 18", secondary: "12:35 AM" },
          },
          {
            rail: "ok",
            title: "Not scheduled",
            pill: { label: "Completed", tone: "ok" },
            snippet: '"…not scheduled to come into the treatment center on 04/18/2026."',
            when: { primary: "Apr 17", secondary: "12:35 AM" },
          },
        ]}
        footer={{ summary: <>Showing 4 of 34</>, action: { label: "See all →", href: "#" } }}
      />
    </PreviewShell>
  );
}

// ============================================================================
// BHG — dashboard (clear)
// ============================================================================
export function BhgDashboardClearPreview() {
  return (
    <PreviewShell
      productLabel="Check-in Assist"
      username="dreyes"
      headerCrumb={{ crumbs: ["Dashboard"], emphasized: "Check-in Assist" }}
      headerStatus={<StatusChip>Connected · synced 5:04 AM</StatusChip>}
    >
      <PageHeader
        headingLevel="h2"
        title={<>Good morning, dreyes.</>}
        sub="Today's schedule, your latest call result, and a record of every check-in."
        meta={{
          label: "Today",
          value: "Tue, Apr 21, 2026",
          subValue: "05:04 AM · America/Denver",
        }}
      />
      <DashboardHero cols={3}>
        <HeroCell
          cell="visit"
          kickerIcon={
            <Icon tone="success">
              <IconCheck />
            </Icon>
          }
          kickerLabel="Visit status"
          big="Not scheduled"
          desc="You're not scheduled to visit the treatment center today."
        />
        <HeroCell
          cell="checkin"
          kickerIcon={
            <Icon tone="success">
              <IconCheck />
            </Icon>
          }
          kickerLabel="Today's check-in"
          big="Call completed."
          bigTone="success"
          desc="Your 5:00 AM call went through."
        />
        <HeroCell
          cell="dosing"
          kickerIcon={
            <Icon tone="warn">
              <IconClock />
            </Icon>
          }
          kickerLabel="Clinic dosing"
          big="Closes 3:00 PM"
          desc="Nothing to report — enjoy your day."
        />
      </DashboardHero>
      <div className="grid">
        <ClinicSnapshot
          editAction={{ label: "Edit clinic →", href: "#" }}
          name="BHG Denver Downtown Treatment Center"
          addressLines={["5250 Leetsdale Drive, Suite 220 · Denver, CO 80246"]}
          phone="(303) 629-5293"
          hours={[
            { label: "Operating hours", value: "5:00a – 3:00p" },
            { label: "Medication hours", value: "5:00a – 3:00p" },
          ]}
          link={{ label: "View clinic details", href: "#" }}
        />
        <AtAGlance meta="updated 5:04 AM">
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Tomorrow's visit"
            subtitle="Updates after the next completed call"
            value="Not scheduled"
            valueNote="confirmed"
          />
          <GlanceRow
            icon={<IconClock />}
            iconTone="mute"
            title="Next call"
            subtitle="Wed, Apr 22 — based on your schedule"
            value="5:00 AM"
            valueNote="in 23h 56m"
          />
          <GlanceRow
            icon={<IconMail />}
            iconTone="brand"
            title="Notification delivery"
            subtitle="SMS + Email · daily at 05:00"
            value="On"
            valueNote="2 channels"
          />
        </AtAGlance>
      </div>
      <RecentCallLog
        wide
        meta="LAST 4 DAYS"
        rows={[
          {
            rail: "ok",
            title: "Not scheduled",
            pill: { label: "Completed", tone: "ok" },
            snippet: '"…not scheduled to come into the treatment center on 04/21/2026."',
            when: { primary: "Apr 21", secondary: "5:00 AM" },
          },
          {
            rail: "ok",
            title: "Not scheduled",
            pill: { label: "Completed", tone: "ok" },
            snippet: '"…not scheduled to come into the treatment center on 04/20/2026."',
            when: { primary: "Apr 20", secondary: "5:00 AM" },
          },
          {
            rail: "fail",
            title: "Drug Screen",
            pill: { label: "Scheduled", tone: "scr" },
            snippet: '"…scheduled to visit the treatment center by end of dosing hours."',
            when: { primary: "Apr 19", secondary: "5:00 AM" },
          },
        ]}
        footer={{ summary: <>Showing 3 of 34</>, action: { label: "See all →", href: "#" } }}
      />
    </PreviewShell>
  );
}

// ============================================================================
// BHG — call detail (scheduled)
// ============================================================================
export function BhgCallDetailPreview() {
  return (
    <PreviewShell
      productLabel="Check-in Assist"
      username="kvance"
      headerCrumb={{ crumbs: ["Dashboard", "Call detail"], emphasized: "Apr 20 · 12:35 AM" }}
      headerStatus={<StatusChip>Completed</StatusChip>}
    >
      <CallDetailPage>
        <CallDetailNav onBack={() => undefined} crumb={<>Apr 20, 2026 · 12:35 AM</>} />
        <CallDetailBody>
          <OutcomeHero
            variant="danger"
            kickerLabel="Action required"
            headline={
              <>
                You are scheduled to visit <em>BHG Denver Downtown</em> today for Drug Screen.
              </>
            }
            sub="Go in before dosing closes at 3:00 PM on Apr 20, 2026."
            meta={[
              { label: "Result", value: <ResultPill tone="scr">Drug Screen</ResultPill> },
              { label: "Status", value: <ResultPill tone="ok">completed</ResultPill> },
              { label: "Call SID", value: "CA-2026-001" },
            ]}
          />
          <NextStepBanner
            title="Arrive at BHG Denver Downtown before 3:00 PM."
            description="5250 Leetsdale Drive, Suite 220 · Denver, CO 80246 · (303) 629-5293"
          />
          <AudioPlayer durationSeconds={68} />
          <CallDetailColumns>
            <TranscriptContainer>
              <TranscriptRow side="ivr" time="12:35" timeLabel="AM" who="Clinic line">
                Thank you for calling Behavioral Health Group Denver Downtown treatment center.
              </TranscriptRow>
              <TranscriptRow side="us" time="12:35" timeLabel="AM" who="ConstructorFleet">
                Entered patient identifiers and confirmed selection.
              </TranscriptRow>
              <TranscriptRow side="ivr" time="12:36" timeLabel="AM" who="Clinic line" resolution>
                You are scheduled to visit the treatment center by the end of dosing hours on
                04/20/2026 for a urine screen.
              </TranscriptRow>
            </TranscriptContainer>
            <CallDetailRail>
              <RelatedCallsCard>
                <RelatedCallRow
                  tone="ok"
                  title="Not scheduled"
                  subtitle="…not scheduled to come into the treatment center on 04/19/2026."
                  time="12:35 AM"
                />
                <RelatedCallRow
                  tone="ok"
                  title="Not scheduled"
                  subtitle="…not scheduled to come into the treatment center on 04/18/2026."
                  time="12:35 AM"
                />
                <RelatedCallRow
                  tone="fail"
                  title="Drug Screen"
                  subtitle="…scheduled to visit the treatment center by end of dosing hours."
                  time="12:35 AM"
                />
              </RelatedCallsCard>
            </CallDetailRail>
          </CallDetailColumns>
        </CallDetailBody>
      </CallDetailPage>
    </PreviewShell>
  );
}

// ============================================================================
// UA — dashboard (report today)
// ============================================================================
export function UaDashboardReportPreview() {
  return (
    <PreviewShell
      productLabel="UA Notifications"
      username="tharrow"
      headerCrumb={{ crumbs: ["Dashboard"], emphasized: "UA Notifications" }}
      headerStatus={<StatusChip>Connected · synced 6:02 AM</StatusChip>}
    >
      <PageHeader
        headingLevel="h2"
        title={<>Good morning, tharrow.</>}
        sub="Today's color, your latest call result, and a record of every check-in."
        meta={{
          label: "Today",
          value: "Tue, Apr 21, 2026",
          subValue: "06:02 AM · America/Denver",
        }}
      />
      <DashboardHero state="scheduled" cols={3}>
        <HeroCell
          cell="visit"
          kickerIcon={
            <Icon tone="danger">
              <IconAlert />
            </Icon>
          }
          kickerLabel="Report today"
          big="RED · Report by 4 PM"
          bigTone="danger"
          desc="Your color came up. Report to the testing site before 4:00 PM."
        />
        <HeroCell
          cell="checkin"
          kickerIcon={
            <Icon tone="success">
              <IconCheck />
            </Icon>
          }
          kickerLabel="Today's check"
          big="Line checked."
          bigTone="success"
          desc="Your 6:00 AM call completed."
        />
        <HeroCell
          cell="dosing"
          kickerIcon={
            <Icon tone="warn">
              <IconClock />
            </Icon>
          }
          kickerLabel="Site hours"
          big="9a – 4p"
          desc="Boulder County probation drop-in."
        />
      </DashboardHero>
      <div className="grid">
        <AtAGlance title="This week" meta="updated 6:04 AM">
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Checks complete"
            subtitle="Boulder County — Colors program"
            value="3 of 5"
            valueNote="on schedule"
          />
          <GlanceRow
            icon={<IconClock />}
            iconTone="mute"
            title="Next call"
            subtitle="Tomorrow — 6:00 AM"
            value="22h 54m"
            valueNote="until next"
          />
          <GlanceRow
            icon={<IconDashboard />}
            iconTone="brand"
            title="Your color"
            subtitle="Assigned by the court"
            value="RED"
            valueNote="active"
          />
        </AtAGlance>
        <AtAGlance title="Notifications" meta="updated 6:04 AM">
          <GlanceRow
            icon={<IconMail />}
            iconTone="brand"
            title="Delivery"
            subtitle="SMS + Email · daily at 06:00"
            value="On"
            valueNote="2 channels"
          />
        </AtAGlance>
      </div>
      <RecentCallLog
        wide
        meta="LAST 5 CHECKS"
        rows={[
          {
            rail: "fail",
            title: "Apr 21 — RED",
            pill: { label: "You reported", tone: "rep" },
            snippet: "Color: RED. Report to the testing site by 4:00 PM.",
            when: { primary: "Apr 21", secondary: "6:02 AM" },
          },
          {
            rail: "ok",
            title: "Apr 20 — BLUE",
            pill: { label: "No report", tone: "default" },
            snippet: "Color: BLUE. No action required.",
            when: { primary: "Apr 20", secondary: "6:02 AM" },
          },
          {
            rail: "ok",
            title: "Apr 19 — GREEN",
            pill: { label: "No report", tone: "default" },
            snippet: "Color: GREEN. No action required.",
            when: { primary: "Apr 19", secondary: "6:02 AM" },
          },
        ]}
        footer={{ summary: <>Showing 3 of 12</>, action: { label: "See all →", href: "#" } }}
      />
    </PreviewShell>
  );
}

// ============================================================================
// UA — dashboard (clear)
// ============================================================================
export function UaDashboardClearPreview() {
  return (
    <PreviewShell
      productLabel="UA Notifications"
      headerCrumb={{ crumbs: ["Dashboard"], emphasized: "UA Notifications" }}
      headerStatus={<StatusChip>Connected · synced 6:02 AM</StatusChip>}
    >
      <PageHeader
        headingLevel="h2"
        title={<>Good morning, ajanis.</>}
        sub="Today's color, your latest call result, and a record of every check-in."
        meta={{
          label: "Today",
          value: "Tue, Apr 21, 2026",
          subValue: "06:02 AM · America/Denver",
        }}
      />
      <DashboardHero cols={2}>
        <HeroCell
          cell="visit"
          kickerIcon={
            <Icon tone="success">
              <IconCheck />
            </Icon>
          }
          kickerLabel="Today's call"
          big="No report needed"
          desc="Your color did not come up. Check again tomorrow."
        />
        <HeroCell
          cell="checkin"
          kickerIcon={
            <Icon tone="mute">
              <IconCalendar />
            </Icon>
          }
          kickerLabel="Next call"
          big="Tomorrow · 6:00 AM"
          desc="We'll call the line and text you the result."
        />
      </DashboardHero>
      <div className="grid">
        <AtAGlance title="This week" meta="updated 6:04 AM">
          <GlanceRow
            icon={<IconCalendar />}
            iconTone="mute"
            title="Checks complete"
            subtitle="Boulder County — Colors program"
            value="3 of 5"
            valueNote="on schedule"
          />
          <GlanceRow
            icon={<IconClock />}
            iconTone="mute"
            title="Next call"
            subtitle="Tomorrow — 6:00 AM"
            value="22h 54m"
            valueNote="until next"
          />
          <GlanceRow
            icon={<IconDashboard />}
            iconTone="brand"
            title="Your color"
            subtitle="Assigned by the court"
            value="RED"
            valueNote="active"
          />
        </AtAGlance>
        <AtAGlance title="Notifications" meta="updated 6:04 AM">
          <GlanceRow
            icon={<IconMail />}
            iconTone="brand"
            title="Delivery"
            subtitle="SMS + Email · daily at 06:00"
            value="On"
            valueNote="2 channels"
          />
        </AtAGlance>
      </div>
      <RecentCallLog
        wide
        meta="LAST 5 CHECKS"
        rows={[
          {
            rail: "ok",
            title: "Apr 21 — BLUE",
            pill: { label: "No report", tone: "default" },
            snippet: "Color: BLUE. No action required.",
            when: { primary: "Apr 21", secondary: "6:02 AM" },
          },
          {
            rail: "ok",
            title: "Apr 20 — GREEN",
            pill: { label: "No report", tone: "default" },
            snippet: "Color: GREEN. No action required.",
            when: { primary: "Apr 20", secondary: "6:02 AM" },
          },
          {
            rail: "fail",
            title: "Apr 19 — RED",
            pill: { label: "You reported", tone: "rep" },
            snippet: "Color: RED. Reported by 3:40 PM.",
            when: { primary: "Apr 19", secondary: "6:02 AM" },
          },
        ]}
        footer={{ summary: <>Showing 3 of 12</>, action: { label: "See all →", href: "#" } }}
      />
    </PreviewShell>
  );
}

// ============================================================================
// BHG — mobile (clear)
// ============================================================================
export function BhgMobileClearPreview() {
  return (
    <div className="hv-surface hv-surface-phone">
      <MobileAppHeader
        greeting="Good morning, dreyes"
        title="Check-in Assist"
        subtitle="Tue, Apr 21 · 5:04 AM"
        trailing={<StatusChip>Synced</StatusChip>}
      />
      <MobileCard tone="success">
        <div className="m-card-kicker">
          <IconCheck /> Not scheduled
        </div>
        <div className="m-card-big">You're clear today.</div>
        <div className="m-card-desc">No visit required — enjoy your day.</div>
      </MobileCard>
      <MobileCard>
        <div className="m-card-kicker">
          <IconClock /> Today's check-in
        </div>
        <div className="m-card-big">Call completed</div>
        <div className="m-card-desc">Your 5:00 AM call went through.</div>
      </MobileCard>
      <MobileTabBar
        items={[
          { icon: <IconDashboard />, label: "Home", active: true },
          { icon: <IconCalendar />, label: "History" },
          { icon: <IconSettings />, label: "Settings" },
        ]}
      />
    </div>
  );
}

// ============================================================================
// UA — mobile (clear)
// ============================================================================
export function UaMobileClearPreview() {
  return (
    <div className="hv-surface hv-surface-phone">
      <MobileAppHeader
        greeting="Good morning, ajanis"
        title="UA Notifications"
        subtitle="Tue, Apr 21 · 6:02 AM"
        trailing={<StatusChip>Synced</StatusChip>}
      />
      <MobileCard tone="success">
        <div className="m-card-kicker">
          <IconCheck /> No report needed
        </div>
        <div className="m-card-big">Your color did not come up.</div>
        <div className="m-card-desc">Check again tomorrow at 6:00 AM.</div>
      </MobileCard>
      <MobileCard>
        <div className="m-card-kicker">
          <IconDashboard /> Your color
        </div>
        <div className="m-card-big">RED</div>
        <div className="m-card-desc">Assigned by the court · Boulder County</div>
      </MobileCard>
      <MobileTabBar
        items={[
          { icon: <IconDashboard />, label: "Home", active: true },
          { icon: <IconCalendar />, label: "History" },
          { icon: <IconSettings />, label: "Settings" },
        ]}
      />
    </div>
  );
}

// ============================================================================
// Dashboard quadrant preview — top-left corner with fade-out edges
// ============================================================================
export function DashboardQuadrantPreview({ children }: { children: React.ReactNode }) {
  return (
    <div className="quad-preview">
      <div className="quad-preview-inner">{children}</div>
    </div>
  );
}

// ============================================================================
// Phone pair — two top-half phone frames side by side
// ============================================================================
export function PhonePairPreview({
  children,
  deepScrollPx = -1380,
  includePhoneFrame = true,
}: {
  children: [React.ReactNode, React.ReactNode];
  deepScrollPx?: number;
  includePhoneFrame?: boolean;
}) {
  return (
    <div className="phone-pair">
      {children.map((child, i) => (
        <div key={i} className="phone-pair-item">
          {includePhoneFrame ? (
            <PhoneFrame width={400} height={700} time="5:04">
              <div className="phone-pair-content">
                <div
                  className={`phone-pair-scale${i === 1 ? " phone-pair-scale-deep" : ""}`}
                  style={i === 1 ? { marginTop: deepScrollPx } : undefined}
                >
                  {child}
                </div>
              </div>
            </PhoneFrame>
          ) : (
            child
          )}
        </div>
      ))}
    </div>
  );
}

// Silence unused-var lint warnings for imported-but-optional items
void Pill;
